// What the client sees: where their case stands, what is coming, who to
// call. Plain language, no case-management jargon, and nothing the firm
// has not decided to share.

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchClientCase, getClientToken } from "../../api/clientPortal";
import { useScreens } from "../../components/chrome/screens";
import { PortalHeader } from "../../components/portal/PortalHeader";
import { formatPortalDate } from "../../components/portal/portalDate";
import { useExpiredSession, usePortalSignOut } from "../../components/portal/portalSession";
import type { ClientPortalDeadline } from "@duralex/shared";

const DEADLINE_TONE: Record<ClientPortalDeadline["status"], string> = {
  critical: "var(--red-700)",
  urgent: "var(--red-600)",
  caution: "var(--warn-600)",
  normal: "var(--muted)",
};

function formatDate(iso: string | null): string {
  return formatPortalDate(iso) ?? "Not scheduled yet";
}

function daysLabel(days: number): string {
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

export default function PortalDashboard() {
  const signOut = usePortalSignOut();
  const token = getClientToken();

  // Keyed by the session, not just by "the portal": on a shared device the
  // next client must never be handed the previous one's cached case.
  const { data, isLoading, error } = useQuery({
    queryKey: ["portal", "case", token],
    queryFn: fetchClientCase,
    retry: false,
  });

  // What a module wants the client to see first (an open bill); each card
  // reads its own data, so a failure there never costs the case view. The
  // cards mount while the case loads too, so their requests leave with the
  // case request instead of one round trip behind it; a card draws nothing
  // until it has something to say.
  const cards = useScreens().flatMap((s) =>
    (s.portalCards ?? []).map((Card, i) => <Card key={`${s.id}-${i}`} />),
  );

  const expired = useExpiredSession(error);

  // The header and the body keep their places across the branches, so a
  // card mounted while the case loads survives its arrival instead of
  // mounting twice and asking twice.
  if (isLoading || expired) {
    return (
      <main className="portal">
        <PortalHeader />
        <div className="portal-body">
          {cards}
          <div className="portal-card portal-loading">Loading your case...</div>
        </div>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="portal">
        <PortalHeader />
        <div className="portal-body">
          <div className="portal-card">
            <p>We could not load your case right now. Please try again, or call our office.</p>
            <button className="portal-link-btn" onClick={signOut}>
              Sign in again
            </button>
          </div>
        </div>
      </main>
    );
  }

  const openDeadlines = data.deadlines.filter((d) => d.daysRemaining >= 0);
  const stillNeeded = data.documentsNeeded.filter((d) => !d.uploaded);

  // Only what the office actually holds - an absent field renders no row,
  // never a placeholder that reads as fact.
  const details = data.details;
  const detailRows: Array<{ label: string; value: string; mono?: boolean }> = [];
  if (data.clientName) detailRows.push({ label: "Name", value: data.clientName });
  if (details.alienNumber)
    detailRows.push({ label: "A-number", value: details.alienNumber, mono: true });
  if (details.phone) detailRows.push({ label: "Phone", value: details.phone });
  if (details.email) detailRows.push({ label: "Email", value: details.email });
  if (details.address) detailRows.push({ label: "Address", value: details.address });

  return (
    <main className="portal">
      <PortalHeader />

      <div className="portal-body">
        {cards}
        <section className="portal-card">
          <h2>Where your case stands</h2>
          <dl className="portal-facts">
            <div>
              <dt>Stage</dt>
              <dd>{data.caseStage ?? "In progress"}</dd>
            </div>
            <div>
              <dt>Case ID</dt>
              <dd className="mono">{data.eId ?? "-"}</dd>
            </div>
            {data.caseNumber && (
              <div>
                <dt>Case number</dt>
                <dd className="mono">{data.caseNumber}</dd>
              </div>
            )}
          </dl>
        </section>

        <section className="portal-card">
          <h2>Your next hearing</h2>
          {data.nextHearing?.date ? (
            <>
              <p className="portal-lead">{formatDate(data.nextHearing.date)}</p>
              {data.nextHearing.hearingType && <p>{data.nextHearing.hearingType}</p>}
              <p className="portal-note">
                Our office will confirm the time and place with you before the date.
              </p>
            </>
          ) : (
            <p className="portal-note">
              Nothing is scheduled yet. We will tell you as soon as a date is set.
            </p>
          )}
        </section>

        <section className="portal-card">
          <h2>What is coming up</h2>
          {openDeadlines.length === 0 ? (
            <p className="portal-note">Nothing needs your attention right now.</p>
          ) : (
            <ul className="portal-list">
              {openDeadlines.map((d) => (
                <li key={`${d.label}-${d.dueDate}`}>
                  <span>{d.label}</span>
                  <span className="portal-when" style={{ color: DEADLINE_TONE[d.status] }}>
                    {formatDate(d.dueDate)} - {daysLabel(d.daysRemaining)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {stillNeeded.length > 0 && (
          <section className="portal-card">
            <h2>Documents we still need from you</h2>
            <ul className="portal-list">
              {stillNeeded.map((d) => (
                <li key={d.type}>
                  <span>{d.description}</span>
                </li>
              ))}
            </ul>
            <p className="portal-note">
              Send them from the <Link to="/portal/documents">Documents</Link> page, or bring them
              to our office.
            </p>
          </section>
        )}

        <section className="portal-card">
          <h2>Your details on file</h2>
          {detailRows.length > 0 ? (
            <dl className="portal-facts">
              {detailRows.map((r) => (
                <div key={r.label}>
                  <dt>{r.label}</dt>
                  <dd className={r.mono ? "mono" : undefined}>{r.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="portal-note">We have no contact details on file for you yet.</p>
          )}
          <p className="portal-note">
            If anything here is wrong or has changed, please call our office so we can correct it.
          </p>
        </section>

        <section className="portal-card">
          <h2>Your legal team</h2>
          <p className="portal-lead">{data.attorney.name ?? "Your attorney"}</p>
          {data.attorney.phone && <p>{data.attorney.phone}</p>}
          {data.attorney.email && <p>{data.attorney.email}</p>}
          <p className="portal-note">Call us any time you are unsure about something.</p>
        </section>
      </div>
    </main>
  );
}
